import { compact } from 'lodash-es';
import { useCallback, useRef } from 'react';
import type { InputsAndDropdownsDrawerDropdownOption } from '../../types';

/**
 * Hook to handle the initial render sorting of options in a multi-select component.
 * It sorts the options alphabetically on the first (creation) render for better display and keeps the order for subsequent renders.
 *
 * - `onSortedUpdate` is a callback that gets called with the sorted selections when the initial render occurs.  This is necessary to ensure that the parent component can update its state with the sorted selections.
 */
export const useInitialRenderMultiSelectSort = (onSortedUpdate: (value: string[], suppressTouch: boolean) => void) => {
  const isInitial = useRef(true);
  const sortOnInitialRender = useCallback(
    (availableOptions: InputsAndDropdownsDrawerDropdownOption<string>[], selectedOptions: string[] | undefined) => {
      const optionsSet = new Map(availableOptions.map(option => [option.value, option]));

      let resultSelection: InputsAndDropdownsDrawerDropdownOption<string>[] = [];
      // Need to come back to this - for now we are not sorting on initial render
      const selectionsUnsorted = compact((selectedOptions ?? []).map(value => optionsSet.get(value)));
      if (isInitial.current) {
        isInitial.current = false;
        resultSelection = selectionsUnsorted.toSorted((a, b) => a.label.localeCompare(b.label));
        if (onSortedUpdate) {
          // Ensure that the update is called after the memo event loop
          setTimeout(() => {
            onSortedUpdate(
              resultSelection.map(option => option.value),
              true
            ); // Pass true to suppress touching during initial sort
          }, 0);
        }
      } else {
        resultSelection = selectionsUnsorted;
      }

      return {
        selections: resultSelection,
        options: availableOptions,
      };
    },
    [onSortedUpdate]
  );
  return {
    initialRenderSort: sortOnInitialRender,
  };
};
